import Bowser from 'bowser';
import dayjs from 'dayjs';
import { FixedPricePrefixType } from '~/api/services/dashboard';
import i18n from '~/components/i18n';
import notify from '~/components/Notify';
import {
  ADMIN_ROLE_SLUG,
  FARMER_MANAGER_ROLE_SLUG,
  FARMER_ROLE_SLUG,
  FAT_PROTEIN_SLUG,
  IFE_ROLE_SLUG,
  LIGHT_USER_ROLE_SLUG,
  MANAGER_ROLE_SLUG,
  MILK_FIXED_SLUG,
  MILK_STOCK_EXCHANGE,
  OPERATIVE_ROLE_SLUG
}
  from '~/Constants';
import { RouteTypes } from '~/routes/constants';
import EnglandFlag from '@assets/svgs/england.svg'
import FranceFlag from '@assets/svgs/france.svg'
import GermanyFlag from '@assets/svgs/germany.svg'
import DutchFlag from '@assets/svgs/netherlands.svg'
import BelgiumFlag from '@assets/svgs/belgium.svg'

export function inDev(): boolean {
  return process.env.NODE_ENV == 'development';
}

export function formatNumber(number, decimalsNumber = 2, replaceNullValue = '') {
  const isSwissDairy = JSON.parse(localStorage.getItem('theme'))?.chEnv === 1
  const localeCode = isSwissDairy ? 'de-CH' : 'de'
  const numberFormated = Number(number).toLocaleString(localeCode, { minimumFractionDigits: decimalsNumber, maximumFractionDigits: decimalsNumber })
  if (!Number.isNaN(numberFormated) && replaceNullValue !== '') {
    return replaceNullValue
  }
  return numberFormated
}

export function formatNumberWithThreeDigitPrecision(number) {
  const numberFormated = Number(number).toLocaleString('de', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
  const lastChar = numberFormated.slice(-1)
  if (lastChar === '5') {
    const string = numberFormated.slice(0, -1);
    return string
  }
  return formatNumber(number)
}

export function formatNumberWithoutFractions(number) {
  return Number(number).toLocaleString('de', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

export function formatNumberOneDecimal(number) {
  return Number(number).toLocaleString('de', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

export function b64toBlob(dataURI, type) {

  const byteString = atob(dataURI.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: type });
}

export function getRoute(
  roleSlug: string,
  operativeUsersCanSeeFarmersInfo: number | boolean,
  userNoFixedPriceNoCoModules?: boolean
): string {
  switch (roleSlug) {
    case ADMIN_ROLE_SLUG:
      return RouteTypes.DAIRY_FACTORY_PROFILE_USERS
    case LIGHT_USER_ROLE_SLUG:
      return RouteTypes.DAIRY_FACTORY_PROFILE_LOCATIONS
    case FARMER_ROLE_SLUG:
      return userNoFixedPriceNoCoModules ? RouteTypes.FARMER_PROFILE : RouteTypes.DASHBOARD
    case FARMER_MANAGER_ROLE_SLUG:
      return RouteTypes.DAIRY_FACTORY_FARMERS
    case OPERATIVE_ROLE_SLUG:
      return operativeUsersCanSeeFarmersInfo ? RouteTypes.DAIRY_FACTORY_PROFILE_USERS : RouteTypes.DAIRY_FACTORY_PROFILE_LOCATIONS
    case IFE_ROLE_SLUG:
      return RouteTypes.MILK_FIXED_PRICE
    case MANAGER_ROLE_SLUG:
      return RouteTypes.MANAGER_DASHBOARD
    default:
      return RouteTypes.MILK_FIXED_PRICE
  }
}

export function casheLanguage(systemDataContext): void {
  const languageStored = localStorage.getItem('i18nextLng')
  if (languageStored) {
    dayjs.locale(languageStored)
    i18n.changeLanguage(languageStored)
  } else if (systemDataContext?.code) {
    dayjs.locale(systemDataContext?.code)
    i18n.changeLanguage(systemDataContext?.code)
  } else {
    dayjs.locale('de')
    i18n.changeLanguage('de')
  }
}

export function splitEvery(array, length) {
  return array.reduce(
    (result, item, index) => {
      if (index % length === 0) result.push([])
      result[Math.floor(index / length)].push(item)
      return result
    }, [])
}

export function formatFileSize(size: number) {
  const units = ['B', 'KB', 'MB', 'GB'];
  let unit = 0;

  while (size >= 1024) {
    size /= 1024;
    unit++;
  }

  return `${size?.toFixed(2) || 0} ${units[unit]}`;
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getMonths(): string[] {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const currentMonth = dayjs().month(i);
    months.push(currentMonth.format('MMMM'));
  }
  return months
}

export function sortAlphabetically(array) {
  const sortedItems = array.sort((a, b) => {
    const titleA = a.title.toUpperCase(); // convert to uppercase to make the comparison case-insensitive
    const titleB = b.title.toUpperCase();

    if (titleA < titleB) {
      return -1;
    }
    if (titleA > titleB) {
      return 1;
    }

    // If titles are equal
    return 0;
  })

  return sortedItems
}

export function browserCheck(): boolean {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const isSupported =
    browser.satisfies({
      chrome: '>=50',
      firefox: '>=50',
      edge: '>=80',
      safari: '>=10'
    })

  return isSupported;
}

function base64ToBlob(base64: string): Blob {
  const parts = base64.split(';base64,');
  const mimeType = parts.length > 1 ? parts[0].replace('data:', '') : 'image/jpeg';
  const raw = parts.length > 1 ? parts[1] : parts[0];
  const bytes = atob(raw);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mimeType });
}

export async function compressImage(base64Image: string, maxWidth: number, maxHeight: number, quality: number, type?: string): Promise<string> {
  const blob = base64ToBlob(base64Image);
  const bitmap = await createImageBitmap(blob);

  let width = bitmap.width;
  let height = bitmap.height;

  if (width > maxWidth) {
    height *= maxWidth / width;
    width = maxWidth;
  }
  if (height > maxHeight) {
    width *= maxHeight / height;
    height = maxHeight;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width);
  canvas.height = Math.round(height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas 2d context');

  ctx.drawImage(bitmap, 0, 0, Math.round(width), Math.round(height));
  bitmap.close();

  return canvas.toDataURL(type || 'image/jpeg', quality);
}

export function formatDateGMT(date) {
  if (!date) {
    return date
  }

  const arrayOfValues = date?.split('.')
  if (arrayOfValues?.length > 0) {
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }
  return date
}

export function getBase64SizeInMB(base64String: string): number {
  const sizeInBytes = (base64String.length * (3 / 4)) - (base64String.indexOf('=') * (3 / 4));
  const sizeInKB = sizeInBytes / 1024;
  return sizeInKB / 1024;
}

export function onError(data): void {
  if (data.response.data) {
    handleErrors(data);
  } else {
    notify(i18n.t('General error'), 'error');
  }
}

export function convertDate(date): string {
  const [day, month, yearAndTime] = date.split('.')
  const year = yearAndTime?.split(' ')[0]
  const time = yearAndTime?.split(' ')[1]
  return (`${year}-${month}-${day} ${time || ''}`)
}

export function formatNumbers(number, type) {
  switch (type) {
    case 'whole_number':
      return parseInt(number)
    case 'one_decimal_number':
      return formatNumberOneDecimal(number)
    case 'two_decimals_number':
      return formatNumber(number)
    default:
      return number
  }
}

export function getUsersBrowserData() {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const browserName = browser.getBrowserName();
  const fullVersion = browser.getBrowserVersion();

  const browserNameFinal = browserName
  const browserVersion = fullVersion
  const platformName = `${capitalizeFirstLetter(browser.getPlatformType())}, ${browser.getOS().name} ${browser.getOSVersion()}`
  return {
    browserName: browserNameFinal,
    browserVersion: browserVersion,
    platformName: platformName
  }
}

export function getLastThreeDaysOfMonth() {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const result = [];

  for (let month = 0; month < 12; month++) {
    const lastDayOfMonth = new Date(currentYear, month + 1, 0).getDate();

    for (let day = lastDayOfMonth; day >= 29; day--) {
      const formattedDate = `${String(day).padStart(2, '0')}.${String(month + 1).padStart(2, '0')}.${currentYear}`;
      result.push(formattedDate);
    }
  }

  return result;
}

export function handleErrors(error) {
  if (error.response?.data?.errors) {
    error.response?.data?.errors.map((error, index) => {
      if (index < 5) {
        return notify(error, 'error')
      } else {
        return false
      }
    })
  } else if (error.response?.data?.message) {
    return notify(error.response?.data?.message, 'error');
  } else {
    return notify(i18n.t('General error'), 'error');
  }
}

export const getTradingColumnName = (prefix, priceLabel, amountUnit) => {
  switch (prefix) {
    case FixedPricePrefixType.FatProtein:
      return {
        trading: {
          name: i18n.t('Trading price (€/100kg)'),
          info: i18n.t('Fixed price which was offered by the dairy within the trading slot.')
        },
        priceBrackets: {
          name: i18n.t('Fixed price amount [in kg / in % ]')
        },
        price: {
          name: i18n.t('Fixed price amount (in kg)')
        },
        average: {
          name: i18n.t('Average fixed price (€/100kg)')
        }
      }
    case FixedPricePrefixType.FixedPrice:
      return {
        trading: {
          name: i18n.t('Trading price ({{priceLabel}})', { priceLabel }),
          info: i18n.t('Fixed price which was offered by the dairy within the trading slot.')
        },
        priceBrackets: {
          name: i18n.t('Fixed price amount [in {{amountUnit}} / in % ]', { amountUnit })
        },
        price: {
          name: i18n.t('Fixed price amount (in {{amountUnit}})', { amountUnit })
        },
        average: {
          name: i18n.t('Average fixed price ({{priceLabel}})', { priceLabel })
        }
      }
    case FixedPricePrefixType.StockExchange:
      return {
        trading: {
          name: i18n.t('BLE price ({{priceLabel}})', { priceLabel }),
          info: i18n.t('Price which was offered by the dairy within the trading slot.')
        },
        priceBrackets: {
          name: i18n.t('Hedging amount [in {{amountUnit}} / in % ]', { amountUnit })
        },
        price: {
          name: i18n.t('Hedging amount (in {{amountUnit}})', { amountUnit })
        },
        average: {
          name: i18n.t('Average BLE price ({{priceLabel}})', { priceLabel })
        }
      }
    default:
      return {
        trading: {
          name: '',
          info: ''
        },
        priceBrackets: {
          name: ''
        },
        price: {
          name: ''
        }
      }
  }
}

export function getPrefixData(prefix, amountUnit?) {
  switch (prefix) {
    case FixedPricePrefixType.FatProtein:
      return {
        chartTitle: i18n.t('Fixed price trend'),
        priceTitle: i18n.t('fixed price'),
        title: i18n.t('Fixed price'),
        amount: i18n.t('Fixed price amount'),
        average: i18n.t('Average fixed price'),
        min: i18n.t('Min price'),
        max: i18n.t('Max price'),
        confirm: i18n.t('Confirmed amount (in kg)'),
        volumePriceLabel: i18n.t('fixed price volumes'),
        tradingDetailInfo: null,
        avgFatPrice: i18n.t('Average fat price'),
        avgProteinPrice: i18n.t('Average protein price'),
        slug: FAT_PROTEIN_SLUG
      }
    case FixedPricePrefixType.FixedPrice:
      return {
        chartTitle: i18n.t('Fixed price trend'),
        priceTitle: i18n.t('fixed price'),
        title: i18n.t('Fixed price'),
        amount: i18n.t('Fixed price amount'),
        average: i18n.t('Average fixed price'),
        min: i18n.t('Min price'),
        max: i18n.t('Max price'),
        confirm: i18n.t('Confirmed amount (in {{amountUnit}})', { amountUnit }),
        volumePriceLabel: i18n.t('fixed price volumes'),
        tradingDetailInfo: null,
        slug: MILK_FIXED_SLUG
      }
    case FixedPricePrefixType.StockExchange:
      return {
        chartTitle: i18n.t('BLE price trend'),
        priceTitle: i18n.t('BLE price'),
        title: i18n.t('BLE price'),
        amount: i18n.t('Hedging amount'),
        average: i18n.t('Average hedging amount'),
        min: i18n.t('Min BLE price'),
        max: i18n.t('Max BLE price'),
        confirm: i18n.t('Hedging amount (in {{amountUnit}})', { amountUnit }),
        volumePriceLabel: i18n.t('BLE price volumes'),
        tradingDetailInfo: i18n.t('Detailed overall overview of the fixed price quantities secured by the dairy (as well as BLE price) for the respective delivery months including all hedging activities carried out.'),
        slug: MILK_STOCK_EXCHANGE
      }
    default:
      return {
        chartTitle: '',
        priceTitle: '',
        title: '',
        amount: '',
        average: '',
        min: '',
        max: '',
        confirm: i18n.t('Confirmed amount (in {{amountUnit}})', { amountUnit }),
        volumePriceLabel: i18n.t(''),
        slug: MILK_FIXED_SLUG
      }
  }
}

export function getDateWithTime(date) {
  const [day, month, yearAndTime] = date?.split('.')
  const year = yearAndTime?.split(' ')[0]
  const time = yearAndTime?.split(' ')[1]
  const hour = time?.split(':')[0]
  const minute = time?.split(':')[1]
  return new Date(year, month - 1, day, hour, minute)
}

export function getDateWithoutTime(date) {
  const [day, month, yearAndTime] = date.split('.')
  const year = yearAndTime?.split(' ')[0]
  return new Date(year, month - 1, day)
}

export function getMaxDate(dateMax, format?: string) {
  if (!dateMax) {
    const formatString = format || 'DD.MM.YYYY HH:mm'
    return dayjs(dateMax).format('DD.MM.YYYY HH:mm')
  }
  //trading slot must end in the month it started
  const lastDayOfMonth = dayjs().endOf('month').date();
  const year = dayjs(dateMax).year()
  const month = dateMax.split('.')[1]
  const dateTrans = new Date(year, parseInt(month) - 1, lastDayOfMonth)
  return dayjs(dateTrans).format('DD.MM.YYYY HH:mm')
}

export function getMinDate(dateMax, format?: string) {
  if (!dateMax) {
    const formatString = format || 'DD.MM.YYYY HH:mm'
    return dayjs(dateMax).format('DD.MM.YYYY HH:mm')
  }
  const dateFrom = getDateWithTime(dateMax)
  return dayjs(dateFrom).format('DD.MM.YYYY HH:mm')
}

export function parseDate(value) {
  const arrayOfValues = value.split('.')
  return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
}

export function getStepPercent(activeTab) {
  switch (activeTab) {
    case 0:
      return 0
    case 1:
      return 2
    case 2:
      return 17
    case 3:
      return 30
    case 4:
      return 43
    case 5:
      return 56
    case 6:
      return 75
    case 7:
      return 100
  }
}

export function getYearsOptions(yearsFrom = 20): { value: number, text: string | number }[] {
  const currentYear = dayjs().year()
  return Array.from({ length: yearsFrom }, (_, index) => ({ value: (currentYear - index), text: (currentYear - index) }))
}

export function getFlagSrc(flag, language?) {
  const flagName = flag || language?.code
  switch (flagName) {
    case 'en':
      return EnglandFlag
    case 'fr_BE':
    case 'be':
      return BelgiumFlag
    case 'fr':
      return FranceFlag
    case 'de':
      return GermanyFlag
    case 'nl_BE':
    case 'nl':
      return DutchFlag
    default:
      return EnglandFlag
  }
}

export function parseDateWithTime(date): string {
  const [day, month, yearAndTime] = date?.split('.')
  const year = yearAndTime?.split(' ')[0]
  const time = yearAndTime?.split(' ')[1]
  const hour = time?.split(':')[0]
  const minute = time?.split(':')[1]

  return `${year}-${month}-${day} ${hour}:${minute}`;
}

export function getCorrectLanguageCode(languageCode) {
  switch (languageCode) {
    case 'gb':
      return 'en'
    case 'fr_BE':
    case 'fr_CH':
      return 'fr'
    case 'nl_BE':
      return 'nl'
    case 'de_CH':
      return 'de'
    default:
      return languageCode
  }
}
